import {
  addWeeks,
  differenceInCalendarWeeks,
  isSameWeek,
  isThisWeek,
  isToday,
  subWeeks,
  isFirstDayOfMonth,
  isSameMonth,
  differenceInCalendarMonths,
  isThisMonth,
  format,
  isLastDayOfMonth
} from 'date-fns';
import { IVigoDateRangeReturn } from 'src/@vigo/vigo-daterange';

interface ParsedResult extends IVigoDateRangeReturn {
  filterColumn: string;
  filterColumnIndex: string;
}

/**
 * Parse a filter string into a date range object.
 *
 * - E.g For filter string "orderDate ge datetime'2023-02-21 00:00:00' and orderDate le datetime'2023-02-22  23:59:59'",
 * an IVigoDateRangeReturn object would be returned with 'dateFrom', 'dateTo', and 'selectedRange'.
 *
 * @param filterString string
 * @returns IVigoDateRangeReturn
 */
export default (filterString: string): ParsedResult => {
  const orderColumnRegex = /^\w+/;
  const dateTimeRegex =
    /datetime'(\d{4}-\d{2}-\d{2} \d{2}:\d{2}:\d{2})'.*datetime'(\d{4}-\d{2}-\d{2} \d{2}:\d{2}:\d{2})'/;

  const filterColumn = filterString ? (filterString as string).match(orderColumnRegex)?.[0] : null;

  const match = filterString ? (filterString as string).match(dateTimeRegex) : null;

  const dateFrom = match?.[1] ?? null;
  const dateTo = match?.[2] ?? null;

  return {
    dateFrom: dateFrom ? new Date(dateFrom) : null,
    dateTo: dateTo ? new Date(dateTo) : null,
    selectedRange: dateFrom && dateTo ? getSelectedRange(new Date(dateFrom), new Date(dateTo)) : '',
    filterColumn: filterColumn ? filterColumn : '',
    filterColumnIndex: getOrderByIndex(filterColumn ?? '')
  };
};

/**
 * Get selected range value
 *
 * @param dateFrom
 * @param dateTo
 * @returns string
 */
export const getSelectedRange = (dateFrom: Date, dateTo: Date): string => {
  if (isToday(dateFrom) && isToday(dateTo)) {
    return 'Today';
  }

  if (isThisWeek(dateFrom, { weekStartsOn: 1 }) && isThisWeek(dateTo, { weekStartsOn: 1 })) {
    return 'This Week';
  }

  if (isSameWeek(subWeeks(dateFrom, 1), subWeeks(dateTo, 1), { weekStartsOn: 1 })) {
    return 'Last Week';
  }

  if (
    isThisWeek(addWeeks(dateTo, 1), { weekStartsOn: 1 }) &&
    differenceInCalendarWeeks(dateFrom, dateTo, { weekStartsOn: 1 }) === -1
  ) {
    return 'Last 2 Weeks';
  }

  if (
    isThisWeek(addWeeks(dateTo, 1), { weekStartsOn: 1 }) &&
    differenceInCalendarWeeks(dateFrom, dateTo, { weekStartsOn: 1 }) === -2
  ) {
    return 'Last 3 Weeks';
  }

  if (
    isThisWeek(addWeeks(dateTo, 1), { weekStartsOn: 1 }) &&
    differenceInCalendarWeeks(dateFrom, dateTo, { weekStartsOn: 1 }) === -3
  ) {
    return 'Last 4 Weeks';
  }

  if (
    isThisMonth(dateFrom) &&
    isThisMonth(dateTo) &&
    isSameMonth(dateFrom, dateTo) &&
    isFirstDayOfMonth(dateFrom) &&
    isLastDayOfMonth(dateTo)
  ) {
    return 'This Month';
  }

  if (isLastDayOfMonth(dateTo) && differenceInCalendarMonths(dateFrom, dateTo) === -2) {
    return 'Last 3 Months';
  }

  return `${format(dateFrom, 'E LLL dd yyyy')} - ${format(dateTo, 'E LLL dd yyyy')}`;
};

const getOrderByIndex = (value: string): string => {
  switch (value) {
    case 'collectionDate':
      return '1';
    case 'orderDate':
      return '2';
    default:
      return '0';
  }
};
